import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowDownRight, ArrowUpRight, CalendarDays, Download, FileText, TrendingUp, WalletCards } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;
const UNASSIGNED_MERCHANT_FILTER = '__unassigned__';
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};
const money = (value) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(value || 0));

function Metric({ label, value, icon: Icon, tone = 'gold' }) {
  const colors = tone === 'green' ? 'text-emerald-300 bg-emerald-500/10' : tone === 'red' ? 'text-rose-300 bg-rose-500/10' : 'text-[#F3D77A] bg-[#D4AF37]/10';
  return (
    <div className="border border-white/10 bg-zinc-950/40 p-5">
      <div className={`grid h-9 w-9 place-items-center rounded-sm ${colors}`}><Icon className="h-4 w-4" /></div>
      <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
      <p className="mt-2 text-xl font-semibold text-white">{money(value)}</p>
    </div>
  );
}

function ReportTable({ title, columns, rows, empty = 'Belum ada data pada bulan ini.' }) {
  return (
    <section className="overflow-hidden border border-white/10 bg-zinc-950/40">
      <div className="border-b border-white/10 px-5 py-4"><h3 className="text-sm font-semibold text-white">{title}</h3></div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead><tr className="border-b border-white/10 bg-white/[0.025]">{columns.map((column) => <th key={column.key} className={`px-4 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 ${column.numeric ? 'text-right' : 'text-left'}`}>{column.label}</th>)}</tr></thead>
          <tbody>
            {rows.length ? rows.map((row, index) => (
              <tr key={`${row.item || row.name || 'row'}-${index}`} className="border-b border-white/5 last:border-0">
                {columns.map((column) => <td key={column.key} className={`px-4 py-3 ${column.numeric ? 'text-right tabular-nums text-zinc-200' : 'font-medium text-white'}`}>{column.render ? column.render(row[column.key], row) : row[column.key]}</td>)}
              </tr>
            )) : <tr><td colSpan={columns.length} className="px-5 py-10 text-center text-sm text-zinc-600">{empty}</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function MonthlyCashierReport() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin';
  const [month, setMonth] = useState(currentMonth());
  const [merchantId, setMerchantId] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [report, setReport] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingAccrue, setDownloadingAccrue] = useState(false);

  const buildParams = (filters) => {
    const value = new URLSearchParams({ month: filters.month });
    if (isAdmin && filters.merchantId) value.set('merchant_id', filters.merchantId);
    return value;
  };

  const fetchReport = async () => {
    const nextFilters = { month, merchantId: isAdmin ? merchantId : '' };
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reports/monthly-cashier?${buildParams(nextFilters)}`, { withCredentials: true });
      setReport(data);
      setAppliedFilters(nextFilters);
    } catch (error) {
      console.error('Failed to fetch monthly report:', error);
      setReport(null);
      setAppliedFilters(null);
      alert(error.response?.data?.detail || 'Gagal mengambil laporan bulanan.');
    } finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    if (!appliedFilters) return;
    setDownloading(true);
    try {
      const { data, headers } = await axios.get(`${API}/reports/monthly-cashier/export-pdf?${buildParams(appliedFilters)}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const match = String(headers['content-disposition'] || '').match(/filename="?([^";]+)"?/i);
      const link = document.createElement('a');
      link.href = url; link.download = match?.[1] || `laporan-bulanan-${month}.pdf`;
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download monthly report:', error);
      alert(error.response?.data?.detail || 'Gagal download PDF laporan bulanan.');
    } finally { setDownloading(false); }
  };

  const downloadAccruePdf = async () => {
    if (!appliedFilters) return;
    setDownloadingAccrue(true);
    try {
      const { data, headers } = await axios.get(`${API}/reports/monthly-cashier/export-accrue-pdf?${buildParams(appliedFilters)}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const match = String(headers['content-disposition'] || '').match(/filename="?([^";]+)"?/i);
      const link = document.createElement('a');
      link.href = url;
      link.download = match?.[1] || `accrue-${appliedFilters.month.slice(0, 4)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download accrue report:', error);
      alert(error.response?.data?.detail || 'Gagal download PDF accrue.');
    } finally { setDownloadingAccrue(false); }
  };

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/merchants`, { withCredentials: true }).then(({ data }) => setMerchants(Array.isArray(data) ? data : [])).catch(() => setMerchants([]));
  }, [isAdmin]);
  // Initial report only; later changes are intentionally applied through the Tampilkan button.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReport(); }, []);

  const summary = report?.summary || {};
  const filtersDirty = Boolean(appliedFilters) && (appliedFilters.month !== month || appliedFilters.merchantId !== merchantId);
  return (
    <DashboardLayout>
      <Header title="Laporan Bulanan" />
      <div className="space-y-6 p-8" data-testid="monthly-cashier-report-page">
        <section className="border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.14),transparent_32%),linear-gradient(135deg,rgba(255,255,255,0.035),rgba(9,9,11,0.9))] p-6">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 bg-[#D4AF37]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#F3D77A]"><FileText className="h-3.5 w-3.5" /> Monthly Report</div>
              <h2 className="text-2xl font-semibold text-white">Rekap Operasional Bulanan</h2>
              <p className="mt-2 text-sm leading-6 text-zinc-500">Revenue layanan, penjualan produk, kelas, PT, salary, dan OPEX dalam satu laporan siap PDF.</p>
            </div>
            <div className={`grid gap-3 ${isAdmin ? 'sm:grid-cols-[190px_220px_auto_auto_auto]' : 'sm:grid-cols-[190px_auto_auto_auto]'}`}>
              <div><p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Bulan</p><div className="relative"><CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" /><Input type="month" value={month} onChange={(event) => setMonth(event.target.value)} className="h-11 border-white/10 bg-black/30 pl-10 text-white" data-testid="monthly-report-month" /></div></div>
              {isAdmin && <div><p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Merchant</p><select value={merchantId} onChange={(event) => setMerchantId(event.target.value)} className="h-11 w-full border border-white/10 bg-black/30 px-3 text-sm text-white [&_option]:bg-white [&_option]:text-black"><option value="">Semua Merchant</option>{merchants.map((merchant) => <option key={merchant.id} value={merchant.id}>{merchant.name}</option>)}<option value={UNASSIGNED_MERCHANT_FILTER}>Belum Terhubung (Data Lama)</option></select></div>}
              <Button onClick={fetchReport} disabled={loading || !month} className="self-end bg-[#D4AF37] text-black hover:bg-[#C59A27]">Tampilkan</Button>
              <Button onClick={downloadPdf} disabled={loading || downloading || !report || filtersDirty} variant="outline" className="self-end border-white/10 bg-black/30 text-white hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 hover:text-[#F3D77A]" data-testid="monthly-report-download-pdf"><Download className="mr-2 h-4 w-4" />{downloading ? 'Menyiapkan...' : filtersDirty ? 'Tampilkan Dulu' : 'Download PDF'}</Button>
              <Button onClick={downloadAccruePdf} disabled={loading || downloadingAccrue || !report || filtersDirty} variant="outline" className="self-end border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F3D77A] hover:bg-[#D4AF37]/20" data-testid="monthly-report-download-accrue-pdf"><Download className="mr-2 h-4 w-4" />{downloadingAccrue ? 'Menyiapkan...' : filtersDirty ? 'Tampilkan Dulu' : 'PDF Accrue'}</Button>
            </div>
          </div>
        </section>

        {loading ? <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" /></div> : <>
          {Number(summary.unassigned_records || 0) > 0 && <div className="border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">Ada {summary.unassigned_records} data lama yang belum terhubung merchant. Gunakan filter &quot;Belum Terhubung (Data Lama)&quot; untuk memeriksanya.</div>}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4"><Metric label="Total Revenue" value={summary.revenue} icon={TrendingUp} /><Metric label="Salary" value={summary.salary} icon={WalletCards} tone="red" /><Metric label="OPEX" value={summary.opex} icon={WalletCards} tone="red" /><Metric label="Profit Produk" value={summary.product_profit} icon={TrendingUp} tone="green" /></div>

          {/* Profit Breakdown Section */}
          <div className="grid gap-6 xl:grid-cols-[1.1fr_.9fr]">
            {/* Rincian Profit */}
            <section className="overflow-hidden border border-white/10 bg-zinc-950/40">
              <div className="border-b border-white/10 px-6 py-4">
                <h3 className="text-sm font-semibold text-white">Rincian Profit</h3>
                {summary.merchant_type === 'BLACKFIT_PREMIERE' && <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F3D77A]">BlackFit Premiere</p>}
              </div>
              <div className="divide-y divide-white/5">
                <div className="flex items-center justify-between px-6 py-3.5">
                  <div><span className="text-sm font-semibold text-white">REVENUE</span><p className="mt-0.5 text-[11px] text-zinc-500">Paket BFP & POS</p></div>
                  <span className="text-sm font-semibold text-white tabular-nums">{money(summary.revenue)}</span>
                </div>
                <div className="flex items-center justify-between px-6 py-3.5">
                  <div><span className="text-sm font-semibold text-white">OPEX</span><p className="mt-0.5 text-[11px] text-zinc-500">Payment Request Umum, Expenses (Non Gaji Staff), Petty Cash Out</p></div>
                  <span className="text-sm font-semibold text-red-300 tabular-nums">- {money(summary.opex)}</span>
                </div>
                <div className="flex items-center justify-between px-6 py-3.5">
                  <div><span className="text-sm font-semibold text-white">SALARY</span><p className="mt-0.5 text-[11px] text-zinc-500">Expenses (Gaji Staff)</p></div>
                  <span className="text-sm font-semibold text-red-300 tabular-nums">- {money(summary.salary)}</span>
                </div>
                <div className="flex items-center justify-between px-6 py-3.5">
                  <div><span className="text-sm font-semibold text-white">COACH</span><p className="mt-0.5 text-[11px] text-zinc-500">Rekap Kelas</p></div>
                  <span className="text-sm font-semibold text-red-300 tabular-nums">- {money(summary.total_class_sharing)}</span>
                </div>
                <div className="flex items-center justify-between px-6 py-3.5">
                  <div><span className="text-sm font-semibold text-white">PT</span><p className="mt-0.5 text-[11px] text-zinc-500">Rekap PT</p></div>
                  <span className="text-sm font-semibold text-red-300 tabular-nums">- {money(summary.pt)}</span>
                </div>
                <div className="flex items-center justify-between px-6 py-3.5">
                  <div><span className="text-sm font-semibold text-white">OTHERS</span><p className="mt-0.5 text-[11px] text-zinc-500">Payment Request Tambah Stock</p></div>
                  <span className="text-sm font-semibold text-red-300 tabular-nums">- {money(summary.total_add_stock_payment)}</span>
                </div>
                {summary.merchant_type === 'BLACKFIT_PREMIERE' ? (
                  <div className="flex items-center justify-between px-6 py-3.5">
                    <div><span className="text-sm font-semibold text-white">ELS</span><p className="mt-0.5 text-[11px] text-zinc-500">Total Sharing ELS</p></div>
                    <span className="text-sm font-semibold text-red-300 tabular-nums">- {money(summary.total_els)}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between px-6 py-3.5">
                    <div><span className="text-sm font-semibold text-white">P ROBIN</span><p className="mt-0.5 text-[11px] text-zinc-500">Total P Robin</p></div>
                    <span className="text-sm font-semibold text-red-300 tabular-nums">- {money(summary.p_robin_amount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between border-t border-white/10 bg-white/[0.03] px-6 py-4">
                  <span className="text-sm font-bold text-white">PROFIT</span>
                  <span className={`text-lg font-bold tabular-nums ${(summary.profit || 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{money(summary.profit)}</span>
                </div>
                <div className="flex items-center justify-between px-6 py-3">
                  <span className="text-sm font-semibold text-white">ACCRUE SALARY</span>
                  <span className="text-sm font-semibold text-amber-300 tabular-nums">{money(summary.accrue_salary_fixed)}</span>
                </div>
                <div className="flex items-center justify-between px-6 py-3">
                  <span className="text-sm font-semibold text-white">ACCRUE ALAT</span>
                  <span className="text-sm font-semibold text-amber-300 tabular-nums">{money(summary.accrue_alat_fixed)}</span>
                </div>
                <div className="flex items-center justify-between px-6 py-3">
                  <span className="text-sm font-semibold text-white">ACCRUE PAJAK</span>
                  <span className="text-sm font-semibold text-amber-300 tabular-nums">{money(summary.accrue_pajak_fixed)}</span>
                </div>
                {summary.merchant_type === 'BLACKFIT_PREMIERE' && (
                  <>
                    <div className="flex items-center justify-between px-6 py-3">
                      <span className="text-sm font-semibold text-white">ACCRUE LISTRIK</span>
                      <span className="text-sm font-semibold text-amber-300 tabular-nums">{money(summary.accrue_listrik_fixed)}</span>
                    </div>
                    <div className="flex items-center justify-between px-6 py-3">
                      <span className="text-sm font-semibold text-white">ACCRUE AIR</span>
                      <span className="text-sm font-semibold text-amber-300 tabular-nums">{money(summary.accrue_air_fixed)}</span>
                    </div>
                    <div className="flex items-center justify-between px-6 py-3">
                      <span className="text-sm font-semibold text-white">ACCRUE ABODEMEN LISTRIK</span>
                      <span className="text-sm font-semibold text-amber-300 tabular-nums">{money(summary.accrue_abodemen_listrik_fixed)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between border-t border-white/10 bg-white/[0.03] px-6 py-4">
                  <span className="text-sm font-bold text-white">PROFIT BERSIH</span>
                  <span className={`text-lg font-bold tabular-nums ${(summary.profit_bersih || 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{money(summary.profit_bersih)}</span>
                </div>
                <div className="border-t border-white/10 bg-white/[0.03] px-6 py-4">
                  <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">SHARING PROFIT</p>
                  <div className={`grid gap-4 ${summary.merchant_type === 'BLACKFIT_PREMIERE' ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    <div className="rounded-sm border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-400/70">RSE</p>
                      <p className="mt-1 text-sm font-bold text-emerald-300 tabular-nums">{money(summary.sharing_rse)}</p>
                      <p className="mt-0.5 text-[10px] text-zinc-500">{summary.sharing_rse_pct}% Profit Bersih</p>
                    </div>
                    <div className="rounded-sm border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-400/70">SAN</p>
                      <p className="mt-1 text-sm font-bold text-emerald-300 tabular-nums">{money(summary.sharing_san)}</p>
                      <p className="mt-0.5 text-[10px] text-zinc-500">{summary.sharing_san_pct}% Profit Bersih</p>
                    </div>
                    {summary.merchant_type !== 'BLACKFIT_PREMIERE' && (
                      <div className="rounded-sm border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-400/70">RMA</p>
                        <p className="mt-1 text-sm font-bold text-emerald-300 tabular-nums">{money(summary.sharing_rma)}</p>
                        <p className="mt-0.5 text-[10px] text-zinc-500">{summary.sharing_rma_pct}% Profit Bersih</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Right side: ELS for BFP, P Robin for BF */}
            <section className="overflow-hidden border border-white/10 bg-zinc-950/40">
              {summary.merchant_type === 'BLACKFIT_PREMIERE' ? (
                <>
                  <div className="border-b border-white/10 px-6 py-4">
                    <h3 className="text-sm font-semibold text-white">ELS</h3>
                    <p className="mt-1 text-[11px] text-zinc-500">Sharing ELS berdasarkan revenue per kategori.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-white/10 bg-white/[0.025]">
                        <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Keterangan</th>
                        <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Revenue</th>
                        <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">%</th>
                        <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">ELS</th>
                      </tr></thead>
                      <tbody className="divide-y divide-white/5">
                        {(summary.els_table || []).map((row, i) => (
                          <tr key={`${row.item}-${i}`} className="border-b border-white/5 last:border-0">
                            <td className="px-4 py-3 font-medium text-white">{row.item}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-zinc-200">{money(row.revenue)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-zinc-200">{row.rate * 100}%</td>
                            <td className="px-4 py-3 text-right tabular-nums text-[#F3D77A]">{money(row.els)}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-white/10 bg-white/[0.025] font-semibold">
                          <td className="px-4 py-3 text-white">TOTAL</td>
                          <td className="px-4 py-3 text-right text-white">{money(summary.revenue)}</td>
                          <td className="px-4 py-3"></td>
                          <td className="px-4 py-3 text-right text-[#F3D77A]">{money(summary.total_els)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div className="border-b border-white/10 px-6 py-4">
                    <h3 className="text-sm font-semibold text-white">P Robin</h3>
                    <p className="mt-1 text-[11px] text-zinc-500">BlackFit = Revenue - Coach/Modal. P Robin = 15% x BlackFit.</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px] text-sm">
                      <thead><tr className="border-b border-white/10 bg-white/[0.025]">
                        <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Keterangan</th>
                        <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Revenue</th>
                        <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Coach/Modal</th>
                        <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">BlackFit</th>
                        <th className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">P Robin</th>
                      </tr></thead>
                      <tbody className="divide-y divide-white/5">
                        {(summary.p_robin_table || []).map((row, i) => (
                          <tr key={`${row.item}-${i}`} className="border-b border-white/5 last:border-0">
                            <td className="px-4 py-3 font-medium text-white">{row.item}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-zinc-200">{money(row.revenue)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-zinc-200">{money(row.sharing)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-zinc-200">{money(row.blackfit)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-[#F3D77A]">{money(row.p_robin)}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-white/10 bg-white/[0.025] font-semibold">
                          <td className="px-4 py-3 text-white">TOTAL</td>
                          <td className="px-4 py-3 text-right text-white">{money(summary.revenue)}</td>
                          <td className="px-4 py-3 text-right text-white">{money(summary.total_p_robin_sharing)}</td>
                          <td className="px-4 py-3 text-right text-white">{money(summary.total_p_robin_blackfit)}</td>
                          <td className="px-4 py-3 text-right text-[#F3D77A]">{money(summary.p_robin_amount)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          </div>
          <ReportTable title="Revenue per Item" rows={report?.revenue_rows || []} columns={[{ key: 'item', label: 'Item' }, ...['cardio','gym','others','pt','total'].map((key) => ({ key, label: key === 'total' ? 'Grand Total' : key, numeric: true, render: money }))]} />
          <div className="grid gap-6 xl:grid-cols-2">
            <ReportTable title="Salary" rows={report?.salary_rows || []} columns={[{ key: 'item', label: 'Nama' }, { key: 'amount', label: 'Amount', numeric: true, render: money }]} />
            <ReportTable title="Rekap Kelas" rows={report?.class_rows || []} columns={[{ key: 'item', label: 'Kelas' }, { key: 'done', label: 'Done', numeric: true }, { key: 'not_done', label: 'Not Done', numeric: true }, { key: 'revenue', label: 'Revenue', numeric: true, render: money }, { key: 'sharing_amount', label: 'Sharing', numeric: true, render: money }]} />
            <ReportTable title="OPEX" rows={report?.opex_rows || []} columns={[{ key: 'item', label: 'Item' }, { key: 'category', label: 'Kategori' }, { key: 'amount', label: 'Amount', numeric: true, render: money }]} />
            <ReportTable title="Total PT" rows={report?.pt_rows || []} columns={[{ key: 'name', label: 'Nama PT' }, { key: 'sessions', label: 'Sesi Done', numeric: true }, { key: 'incentive', label: 'Intensif', numeric: true, render: money }, { key: 'amount', label: 'Amount', numeric: true, render: money }]} />
          </div>
          <ReportTable title="Produk / Others" rows={report?.product_rows || []} columns={[{ key: 'item', label: 'Item' }, { key: 'revenue', label: 'Revenue', numeric: true, render: money }, { key: 'quantity', label: 'Qty', numeric: true }, { key: 'payment', label: 'Payment', numeric: true, render: money }, { key: 'profit', label: 'Profit', numeric: true, render: money }]} />
        </>}
      </div>
    </DashboardLayout>
  );
}
